import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Iter 31.211b — Test-mode status banner on /admin.
 *
 * Surfaces the live `TEST_MODE` env value so the operator can see at a
 * glance whether public test-signups are enabled — and crucially, if
 * the env var was typo'd ("test mode" instead of "1"), the raw value
 * is shown back so the misconfiguration is obvious.
 */
export default function TestModeStatusBanner() {
    const [status, setStatus] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        let alive = true;
        api.get("/auth/test-signup-status")
            .then(({ data }) => alive && setStatus(data))
            .catch((e) => alive && setErr(e?.message || "probe failed"));
        return () => { alive = false; };
    }, []);

    if (err && !status) return null;
    if (!status) return null;

    const enabled = !!status.enabled;
    const raw = status.test_mode_raw;

    return (
        <div
            className={`paper-card border p-4 mb-4 ${
                enabled
                    ? "border-stamp-red/40 bg-stamp-red/5"
                    : "border-ink/30"
            }`}
            data-testid="admin-test-mode-banner"
        >
            <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                        Test-mode signup
                    </h3>
                    <p className="font-body text-xs text-ink-soft mt-1 max-w-2xl">
                        Controls whether the public <code>/login</code> shows the
                        "Register as test operative" button to logged-out
                        visitors. Set <code>TEST_MODE=1</code> on the deployed
                        container to enable; anything else (including the literal
                        words "test mode") disables it.
                    </p>
                </div>
                <div className="text-right">
                    <div
                        className={`font-heading text-2xl ${enabled ? "text-stamp-red" : "text-ink-soft"}`}
                        data-testid="test-mode-state"
                    >
                        {enabled ? "ENABLED" : "OFF"}
                    </div>
                    {raw && !enabled && (
                        <div
                            className="font-body text-[10px] tracking-[0.18em] text-stamp-red uppercase mt-1"
                            data-testid="test-mode-raw-value"
                        >
                            Current value: "{raw}"
                        </div>
                    )}
                </div>
            </div>
            <p className="font-body text-xs text-ink-soft mt-2 italic" data-testid="test-mode-reason">
                {status.reason}
            </p>
        </div>
    );
}
